/**
 * utils/minimumOrder.js
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE definition of "is this basket worth sending a rider out for" for the
 * whole web client.
 *
 * A delivered order has to carry at least the market floor in GOODS. Two things
 * about that are easy to get wrong, so they live here once:
 *
 *   • Delivery is not merchandise. A 25,000 basket with a 6,000 fee is still a
 *     25,000 basket, and still short of a 30,000 floor.
 *   • The floor is PER VENDOR. Every vendor in a basket becomes its own order
 *     with its own rider leg, so 20,000 + 20,000 from two vendors is two short
 *     orders, not one that clears. Nothing here ever adds vendors together.
 *
 * The server is the gate: it refuses a short basket at order-create with
 * BELOW_MINIMUM_ORDER_VALUE and a per-vendor `violations` list. This module is
 * the courtesy, so the customer hears it in the cart, where the fix is one tap
 * away, instead of after choosing how to pay.
 */

const toAmount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Merchandise value of ONE cart line, across every line shape the client
 * stores: `qty` in-house, `quantity` everywhere else, plus the per-unit option
 * prices and spec deltas the vendor slices already fold into their totals.
 */
export const lineSubtotal = (line) => {
  if (!line || typeof line !== "object") return 0;
  const options = Array.isArray(line.options) ? line.options : [];
  const optionsTotal = options.reduce((sum, option) => sum + toAmount(option?.price), 0);
  const unit = toAmount(line.price) + toAmount(line.specPriceDelta) + optionsTotal;
  const rawQty = line.qty ?? line.quantity ?? 1;
  return unit * toAmount(rawQty);
};

/**
 * Goods subtotal for ONE vendor's lines.
 *
 * Callers pass lines rather than a slice's `total` on purpose: the in-house
 * cart reducer moves `state.total` by deltas and never recomputes it, so it
 * drifts, and the retailer slice's `total` is the sum of EVERY vendor's cart at
 * once, which is exactly the number this rule must not use.
 */
export const linesSubtotal = (lines) =>
  (Array.isArray(lines) ? lines : []).reduce((sum, line) => sum + lineSubtotal(line), 0);

/**
 * What is still missing before this vendor's lines can be delivered: 0 when the
 * floor is met, otherwise the positive gap.
 *
 * A minimum of 0 means the market has no floor, so everything passes. An empty
 * cart is not a shortfall either: there is nothing to tell somebody who has not
 * started shopping, and the carts already refuse to check out with no items.
 */
export const minimumOrderShortfall = (subtotal, minimum) => {
  const floor = toAmount(minimum);
  if (floor <= 0) return 0;
  const goods = toAmount(subtotal);
  if (goods <= 0) return 0;
  const gap = floor - goods;
  return gap > 0 ? gap : 0;
};

/** True when this vendor's goods subtotal clears the floor (or there is none). */
export const meetsMinimumOrder = (subtotal, minimum) =>
  minimumOrderShortfall(subtotal, minimum) === 0;

/**
 * The ask, in the customer's own currency: "Add UGX 12,000 more to check out".
 * Takes the formatter rather than importing one so the copy follows whichever
 * market the build is for, and so this module stays pure.
 */
export const minimumOrderMessage = (shortfall, format = String) => {
  const gap = toAmount(shortfall);
  if (gap <= 0) return "";
  const render = typeof format === "function" ? format : String;
  return `Add ${render(gap)} more to check out`;
};

/** The reason, once, in plain words. Empty when the market has no floor. */
export const minimumOrderExplainer = (minimum, format = String) => {
  const floor = toAmount(minimum);
  if (floor <= 0) return "";
  const render = typeof format === "function" ? format : String;
  return `The smallest order we can deliver is ${render(floor)} of goods.`;
};

/**
 * The floor that actually applies to a vendor. A vendor may set its own, and a
 * vendor's floor can only ever be HIGHER than the platform's: the platform
 * number is what a delivery costs to run, not a ceiling on what a seller will
 * accept an order for.
 */
export const effectiveMinimum = (platformMinimum, vendorMinimum) =>
  Math.max(toAmount(platformMinimum), toAmount(vendorMinimum));

/**
 * Per-vendor verdicts for a GROUPED basket, for the cart hub: one entry per
 * vendor that falls short, in the order the groups were given, shaped like the
 * server's `violations` so the two read the same in a bug report.
 *
 * `groups` are `{ kind, id, name, items }`. Pass `subtotalOf` when the surface
 * already computes a subtotal of its own, so the notice can never disagree with
 * the number printed beside it, and `minimumOf` when a vendor carries its own
 * floor.
 */
export const basketShortfalls = (groups, minimum, options = {}) => {
  const { subtotalOf, minimumOf, format } = options;
  const readSubtotal =
    typeof subtotalOf === "function" ? subtotalOf : (group) => linesSubtotal(group?.items);
  const readMinimum = typeof minimumOf === "function" ? minimumOf : () => minimum;

  return (Array.isArray(groups) ? groups : []).reduce((out, group) => {
    if (!group) return out;
    const floor = toAmount(readMinimum(group));
    const subtotal = toAmount(readSubtotal(group));
    const shortfall = minimumOrderShortfall(subtotal, floor);
    if (shortfall <= 0) return out;
    out.push({
      key: [group.kind, group.id].filter(Boolean).join(":"),
      vendorId: group.id ?? null,
      vendorName: group.name || "",
      subtotal,
      minimum: floor,
      shortfall,
      message: minimumOrderMessage(shortfall, format),
    });
    return out;
  }, []);
};
